import { BASE_BACKEND_URL, fetchWithAuth } from '../../../auth'
import { useCallback, useEffect, useRef, useState } from 'react'

import { STATUS_LABELS } from '../constants/storeConstants'

const useFetchStoreOverview = ({
  pagination,
  setPagination,
  supplierFilter,
  buyerFilter,
  clientFilter,
  userFilter,
  statusFilter,
  appliedSearch,
  buildFilterQuery,
  onError
}) => {
  const dataAbortRef = useRef(null)
  const filtersAbortRef = useRef(null)
  const hasLoadedDataRef = useRef(false)

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [supplierOptions, setSupplierOptions] = useState([])
  const [buyerOptions, setBuyerOptions] = useState([])
  const [clientOptions, setClientOptions] = useState([])
  const [userOptions, setUserOptions] = useState([])
  const [statusOptions, setStatusOptions] = useState([])

  const fetchData = useCallback(async () => {
    dataAbortRef.current?.abort()

    const controller = new AbortController()
    dataAbortRef.current = controller

    try {
      if (hasLoadedDataRef.current) {
        setTableLoading(true)
      } else {
        setLoading(true)
      }

      const { current_page, per_page } = pagination

      const supplierQuery = buildFilterQuery(supplierFilter, 'supplier_id')
      const buyerQuery = buildFilterQuery(buyerFilter, 'buyer_id')
      const clientQuery = buildFilterQuery(clientFilter, 'client_uid')
      const userQuery = buildFilterQuery(userFilter, 'user_uid')
      const statusQuery = buildFilterQuery(statusFilter, 'status')
      const searchQuery = appliedSearch
        ? `&q=${encodeURIComponent(appliedSearch)}`
        : ''

      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/poc/store_relations_overview?page=${current_page}&per_page=${per_page}${supplierQuery}${buyerQuery}${clientQuery}${userQuery}${statusQuery}${searchQuery}`,
        {
          signal: controller.signal
        }
      )

      const results = response?.results ?? []
      const totalRecords = Number(response?.total_records ?? 0)

      const nextTotalPages = Math.max(1, Math.ceil(totalRecords / per_page))

      setPagination((prev) => {
        if (prev.total_pages === nextTotalPages) {
          return prev
        }

        return {
          ...prev,
          total_pages: nextTotalPages
        }
      })

      setData(results)
      hasLoadedDataRef.current = true
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('fetchData error', err)
        onError?.('error', 'No fue posible cargar las relaciones de tiendas.')
      }
    } finally {
      setLoading(false)
      setTableLoading(false)
      dataAbortRef.current = null
    }
  }, [
    pagination.current_page,
    pagination.per_page,
    setPagination,
    supplierFilter,
    buyerFilter,
    clientFilter,
    userFilter,
    statusFilter,
    appliedSearch,
    buildFilterQuery,
    onError
  ])

  const fetchFilters = useCallback(async () => {
    filtersAbortRef.current?.abort()

    const controller = new AbortController()
    filtersAbortRef.current = controller

    try {
      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/poc/store_relations_overview/filters`,
        {
          signal: controller.signal
        }
      )

      const filters = response?.filters || {}

      setSupplierOptions(filters.suppliers ?? [])
      setBuyerOptions(filters.buyers ?? [])
      setClientOptions(filters.clients ?? [])
      setUserOptions(filters.users ?? [])
      setStatusOptions(
        (filters.statuses ?? []).map((item) => ({
          ...item,
          label: STATUS_LABELS[item.value] || item.label
        }))
      )
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('fetchFilters error', err)
      }
    } finally {
      filtersAbortRef.current = null
    }
  }, [onError])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    fetchFilters()
  }, [fetchFilters])

  useEffect(() => {
    return () => {
      dataAbortRef.current?.abort()
      filtersAbortRef.current?.abort()
    }
  }, [])

  return {
    data,
    loading,
    tableLoading,
    supplierOptions,
    buyerOptions,
    clientOptions,
    userOptions,
    statusOptions,
    fetchData,
    fetchFilters
  }
}

export default useFetchStoreOverview
